import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { GridRowParams } from '@material-ui/data-grid';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
} from '@material-ui/core';
import { toJS } from 'mobx';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { FormInputTypes } from '../../utils/constants';
import {
  LOCATION_DETAILS_TABS,
  locationDetailsTabParameters,
  chargerDetailsTabParameters,
  CHARGER_DETAILS_TABS,
} from '../../utils/routing';
import { Charger, CHARGER_TYPE } from '../../types/Charger';
import { drawerStore } from '../../state/drawerStore';
import TextLink from '../../components/other/TextLink';
import { ConnectorsList } from '../../components/other/Connectors';
import { gridColumn } from '../../utils/gridUtils';
import { ChargerStatusBadge } from '../../components/other/CellBadges';
import { Api } from '../../api';
import ActionsList from '../../components/other/ActionsList';
import { copyTextToClipboard } from '../../utils/utils';
import { greenTextColor } from '../../components/main/react/App';
import credentialStore from '../../state/credentialStore';
import { getUserAutocompleteInput } from '../../components/forms/UserEmailAutocomplete';
import { VMPaginatedGrid } from '../../components/main/VMPaginatedGrid';
import { usePaginatedProvider } from '../../state/paginatedProviderStore';
import { ProviderStore, useProvider } from '../../state/providerStore';
import { ChargerIcon, getTypeForRow } from '../../components/other/ChargerIcon';

const publicChargerCondition = (values) => values.type === CHARGER_TYPE.PUBLIC;
const domicilChargerCondition = (values) => values.type === CHARGER_TYPE.DOMICIL;

const assignChargerTypeInput = [
  {
    name: 'type',
    type: FormInputTypes.select,
    options: [CHARGER_TYPE.PUBLIC, CHARGER_TYPE.DOMICIL],
  },
];

const getPublicChargerInputs = (locationsProvider: ProviderStore) => [
  {
    name: 'locationId',
    label: 'Location',
    type: FormInputTypes.locationNameAutocomplete,
    inputChangeCallback: (inputValue: string) => {
      locationsProvider.setRequest(() => Api.charge.listLocations({ name: inputValue }));
      locationsProvider.reloadResource();
      locationsProvider.setOnFulfilled(() => {
        drawerStore.updateInputOptions('locationId', locationsProvider.data);
      });
    },
    options: locationsProvider.data?.length ? locationsProvider.data : [],
    condition: () => publicChargerCondition(drawerStore.values),
  },
];

const onUserAddInputChange = (
  usersProvider: ProviderStore,
  inputValue: string,
  locationsProvider: ProviderStore,
) => {
  const urlParams: any = { email: inputValue };

  usersProvider.setOnFulfilled(() => {
    drawerStore.setInputs(
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      getAssignChargerCombinedInputs(locationsProvider, usersProvider),
    );
    drawerStore.setValues({ ...drawerStore.values });
  });

  usersProvider.setRequest(() => Api.user.listUsers(urlParams));
  usersProvider.reloadResource();
};

const getUserInput = (usersProvider: ProviderStore, locationsProvider: ProviderStore) => {
  if (credentialStore.isUserAdmin) {
    return getUserAutocompleteInput(usersProvider.data.users, (inputValue: string) =>
      onUserAddInputChange(usersProvider, inputValue, locationsProvider),
    );
  }

  return { name: 'userId', type: FormInputTypes.textField };
};

const getDomicilChargerInputs = (
  usersProvider: ProviderStore,
  locationsProvider: ProviderStore,
) => [
  {
    ...getUserInput(usersProvider, locationsProvider),
    condition: () => domicilChargerCondition(drawerStore.values),
  },
];

const assignChargerIdentifierInput = [{ name: 'identifier', type: FormInputTypes.textField }];

export function getAssignChargerCombinedInputs(
  usersProvider: ProviderStore,
  locationsProvider: ProviderStore,
) {
  return [
    ...assignChargerTypeInput,
    ...getPublicChargerInputs(locationsProvider),
    ...getDomicilChargerInputs(usersProvider, locationsProvider),
    ...assignChargerIdentifierInput,
  ];
}

const ConfirmationDialog = ({ text, dialogOpen, setDialogOpen, confirmCallback }) => (
  <Dialog open={dialogOpen}>
    <DialogContent>
      <Typography>{text}</Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
      <Button
        onClick={() => {
          setDialogOpen(false);
          confirmCallback();
        }}
        autoFocus
        color="primary"
      >
        Confirm
      </Button>
    </DialogActions>
  </Dialog>
);

const UnassignedChargers = observer(() => {
  const unassignedProvider = usePaginatedProvider(undefined, undefined, false);
  const [sortableFields, setSortableFields] = useState([]);

  useEffect(() => {
    if (credentialStore.isUserAdmin) {
      unassignedProvider.setRequest((offset, sortBy, sortOrder) =>
        Api.charge.listUnassignedChargers({ skip: offset, sortBy, sortOrder }),
      );
    } else {
      unassignedProvider.setRequest((offset, sortBy, sortOrder) =>
        Api.charge.listUnassignedChargersForAccount(credentialStore.selectedAccount.id, {
          skip: offset,
          sortBy,
          sortOrder,
        }),
      );
    }
    unassignedProvider.setOnFulfilled((_data, _count, sortable) => {
      if (sortable) {
        setSortableFields(sortable);
      }
    });
    unassignedProvider.reloadResource();
  }, [credentialStore.selectedAccount]);

  const filterCallback = (param?: string, val?: string) => {
    const urlParams: any = {};
    if (param && val) {
      urlParams[param] = val;
    }
    unassignedProvider.setRequest((offset) =>
      Api.charge.listUnassignedChargers({
        ...urlParams,
        skip: offset,
        take: 20,
        sortBy: unassignedProvider.sortBy,
        sortOrder: unassignedProvider.sortOrder,
      }),
    );
    unassignedProvider.reloadResource();
  };

  const locationsProvider = useProvider(Api.charge.listLocations);
  const usersProvider = useProvider(Api.user.listUsers);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogChargerId, setDialogChargerId] = useState(null);

  const confirmAssignCharger = (chargerId: string, values) => {
    if (values.userId) {
      Api.charge
        .assignChargerToUser(chargerId, values.userId, values.identifier)
        .then(unassignedProvider.reloadResource);
    } else if (values.locationId) {
      Api.charge
        .assignChargerToLocation(chargerId, values.locationId, values.identifier)
        .then(unassignedProvider.reloadResource);
    }
  };

  const confirmDeleteCharger = (chargerId: string) => {
    Api.charge.deleteCharger(chargerId).then(unassignedProvider.reloadResource);
  };

  const onClickDeleteCharger = (charger: Charger) => {
    setDialogChargerId(charger.id);
    setDialogOpen(true);
  };

  const onClickAssignCharger = (charger: Charger) => {
    drawerStore.openDrawer({
      label: 'Assign charge point',
      inputs: getAssignChargerCombinedInputs(locationsProvider, usersProvider),
      clickCallback: () => confirmAssignCharger(charger.id, toJS(drawerStore.values)),
    });
  };

  const unassignedChargerActions = (params: GridRowParams) => (
    <ActionsList
      actions={[
        {
          label: 'Assign',
          callback: () => {
            onClickAssignCharger(params.row as Charger);
          },
        },
        {
          label: 'Remove',
          callback: () => {
            onClickDeleteCharger(params.row as Charger);
          },
        },
      ]}
    />
  );

  const unassignedChargersColumns = [
    gridColumn('chargerTypeIcon', {
      width: 60,
      headerName: '',
      sortableFields,
      renderCell: (params: GridRowParams) => {
        const type = getTypeForRow(params.row);
        return <ChargerIcon type={type} />;
      },
    }),
    gridColumn('ocppId', {
      flex: 3,
      headerName: 'OCPP ID',
      sortableFields,
      minWidth: 150,
      renderCell: (params: GridRowParams) => (
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 'normal' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              alignContent: 'center',
              maxHeight: '25px',
            }}
          >
            <TextLink
              to={chargerDetailsTabParameters(params.row.id, CHARGER_DETAILS_TABS.OVERVIEW)}
              label={params.row.ocppId}
            />
            <IconButton
              onClick={() => {
                copyTextToClipboard(params.row.ocppId);
              }}
            >
              <ContentCopyIcon
                style={{ marginLeft: '0.3em', color: greenTextColor, fontSize: 18 }}
              />
            </IconButton>
          </div>
          <span
            style={{ color: 'gray', fontSize: '0.9rem' }}
          >{`${params.row.manufacturer} / ${params.row.model}`}</span>
        </div>
      ),
    }),
    gridColumn('address', {
      flex: 3,
      headerName: 'Location',
      sortableFields,
      minWidth: 150,
      renderCell: (params: GridRowParams) => (
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 'normal' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              alignContent: 'center',
              maxHeight: '25px',
              minHeight: '20px',
            }}
          >
            <TextLink
              to={locationDetailsTabParameters(
                params.row.location?.id,
                LOCATION_DETAILS_TABS.OVERVIEW,
              )}
              label={params.row.location?.name}
            />
          </div>
          <span style={{ color: 'gray', fontSize: '0.9rem' }}>
            {params.row.location?.address ?? ''}
          </span>
        </div>
      ),
    }),
    gridColumn('serial', {
      flex: 2,
      sortableFields,
      minWidth: 150,
    }),
    gridColumn('charging points', {
      flex: 2,
      headerName: 'Charging points',
      sortableFields,
      minWidth: 150,
      renderCell: (params: GridRowParams) => <ConnectorsList connectors={params.row.connectors} />,
    }),
    gridColumn('status', {
      minWidth: 150,
      align: 'center',
      sortableFields,
      renderCell: (params: GridRowParams) => <ChargerStatusBadge status={params.row.status} />,
    }),
    gridColumn('createdAt', { flex: 1, headerName: 'Created at', sortableFields, minWidth: 150 }),
    gridColumn(' ', {
      width: 60,
      renderCell: (params: GridRowParams) => unassignedChargerActions(params),
      align: 'center',
      headerAlign: 'center',
    }),
  ];

  return (
    <div style={{ width: '100%' }}>
      <VMPaginatedGrid
        rows={unassignedProvider.data}
        columns={unassignedChargersColumns}
        label="Unassigned Charge Points"
        count={unassignedProvider.count}
        onPageOffsetChange={unassignedProvider.onPageChange}
        onSortTypeChange={unassignedProvider.setSort}
        filterParamOptions={[
          { label: 'OCPP ID', value: 'ocppId' },
          { label: 'Location', value: 'location' },
        ]}
        filterCallback={filterCallback}
        rowHeight={60}
      />

      <ConfirmationDialog
        text="Are you sure you want to delete charger"
        dialogOpen={dialogOpen}
        setDialogOpen={setDialogOpen}
        confirmCallback={() => confirmDeleteCharger(dialogChargerId)}
      />
    </div>
  );
});

export default UnassignedChargers;
